import { useEffect, useMemo, useState, useRef } from 'react'
import { CategoriaNode } from '@gql/graphql'
import { ListingCollezioniDocument } from '@gql/graphql-get'
import { print } from 'graphql'
import { grapheneGetTranslated } from 'utils'
import {
  PAGINATION_ITEM_PER_PAGE,
  getOrdinamento,
  getFilters,
  fromFiltersToUrl,
} from 'utils/listing'
import { FilterValueType, ListingItemtype } from '@utils/types'
import { useRouter } from 'next/router'
import { useTrans } from '../../../hooks'
import { Button, ButtonRounded, Chip, ResultsLabel, WContainer } from '@components/atoms'
import { Close, Filter, Search } from '@components/icons'
import {
  ListingFilters,
  ListingFiltersMobile,
  ModalNoResult,
  OrderByDropdown,
  ProdottoCard,
  SearchBarListing,
} from '@components/molecules'
import { DescriptionListing } from '@components/organisms'
import styles from './ListingExplorer.module.sass'
import { collezioneToTrack, trackingEcommerceEvent } from '@utils/gtm'

const FILTERS = 'filters'

interface Props {
  className?: string
  listing: ListingItemtype
  categoria?: CategoriaNode
}

const ListingExplorer = (props: Props) => {
  const { className = '', listing: initial = undefined, categoria = undefined } = props

  const [listing, setListing] = useState(initial)
  const [orderBy, setOrderBy] = useState<string>(getOrdinamento(initial))
  const [filters, setFilters] = useState<FilterValueType[]>(getFilters(initial))
  const [searchValue, setSearchValue] = useState<string>(initial?.params?.search || '')
  const [visibleItemsCount, setVisibleItemsCount] = useState(PAGINATION_ITEM_PER_PAGE)
  const [noResults, setNoResults] = useState(false)
  const [searchMobileOpen, setSearchMobileOpen] = useState(false)
  const [filtersMobileOpen, setFiltersMobileOpen] = useState(false)
  const [isFetching, setIsFetching] = useState(false)

  const pageRendered = useRef({ FILTERS: false })
  const t = useTrans()
  const router = useRouter()


  const handleViewListTracking = (
  items: ListingItemtype['elementi'],
  forcedVisibleItemsCount?: number
) => {
  const visibleCount = forcedVisibleItemsCount ?? visibleItemsCount
  const OFFSET = visibleCount === 0 ? 0 : visibleCount - PAGINATION_ITEM_PER_PAGE
  trackingEcommerceEvent('view_item_list', {
    item_list_id: categoria?.pk || 0,
    item_list_name: categoria.slug || '',
    items: items.map((p, index) => collezioneToTrack(p, index + OFFSET, categoria)),
  })
}

  async function fetchListing(page = 1) {
    const filtersGrouped = Object.groupBy(filters, ({ chiave }) => chiave)
    const filtersByCategory = filtersGrouped ? filtersGrouped : []
    const filtersByCategoryValues = Object.values(filtersByCategory)
    const filtersToFetch = filtersByCategoryValues.map((f) => ({
      chiave: f[0].chiave,
      valori: f.map((fv) => ({ id: fv.id })),
    }))

    const listingParams = {
      params: {
        search: searchValue,
        page: page,
        filtri: filtersToFetch,
        ordinamento: {
          value: orderBy,
        },
      },
    }

    const encodedVariables = encodeURIComponent(JSON.stringify(listingParams))
    const encodedQuery = encodeURIComponent(print(ListingCollezioniDocument))
    const response = await grapheneGetTranslated(router.locale).get(
      `?lang=${router.locale}&query=${encodedQuery}&operationName=ListingCollezioni&variables=${encodedVariables}`
    )
    const {
      data: { listingCollezioni },
    } = response.data
    return listingCollezioni
  }

  useEffect(() => {
    if (pageRendered.current[FILTERS]) {
      // previene problemi in caso di richieste lente
      let ignore = false
      const startFetching = async function () {
        setIsFetching(true)
        const listingCollezioni = await fetchListing()
        if (!ignore) {
          const urlParams = new URLSearchParams()

          if (filters.length > 0) {
            const urlParameters = fromFiltersToUrl(filters)
            urlParameters.map((elem) => urlParams.set(elem.k, elem.v))
          }
          if (orderBy) {
            urlParams.append('order', orderBy)
          }

          if (urlParams.toString().length > 0) {
            router.push(router.asPath.split('?')[0] + '?' + urlParams.toString(), undefined, {
              shallow: true,
            })
          } else {
            router.push(router.asPath.split('?')[0], undefined, { shallow: true })
          }

          handleViewListTracking(listingCollezioni.elementi, 0)
          setListing(listingCollezioni)
        }
        setIsFetching(false)
      }
      startFetching()

      return () => {
        ignore = true
      }
    }
    pageRendered.current[FILTERS] = true
  }, [filters, orderBy, searchValue])

  const loadMore = () => {
    if (listing?.numPages && listing?.page) {
      if (listing.numPages > 1 && listing.page < listing.numPages) {
        const startFetching = async function () {
          const page = listing.page + 1
          let listingCollezioni = await fetchListing(page)

        
          setListing({
            ...listingCollezioni,
            elementi: [...listing.elementi, ...listingCollezioni.elementi],
          })

          const newVisibleCount = PAGINATION_ITEM_PER_PAGE * page
          setVisibleItemsCount(newVisibleCount)
          handleViewListTracking(listingCollezioni.elementi, newVisibleCount)
        }
        startFetching()
      }
    }
  }

  const handleClickTracking = (item, index) => {
    trackingEcommerceEvent('select_item', {
      item_list_id: categoria?.pk || 0,
      item_list_name: categoria.slug || '',
      items: [collezioneToTrack(item, index, categoria)],
    })
  }

  // Update processedCards to slice based on visibleItemsCount
  const processedCards = useMemo(() => {
    let cards = listing.elementi || []

    return cards
      .slice(0, visibleItemsCount)
      .map((item, index) => (
        <ProdottoCard key={index} item={item} onClick={() => handleClickTracking(item, index)} />
      ))
  }, [filters, listing, orderBy, visibleItemsCount])

  useEffect(() => {
    handleViewListTracking(listing.elementi)
  }, [categoria])

  return listing ? (
    <>
      <div className={`${styles.root} ${className}`}>
        <WContainer className={styles.container}>
          <div className={styles.filters}>
            <ListingFilters item={listing} filters={filters} setFilters={setFilters} />
          </div>
          <div className={styles.main}>
            <div className={styles.contentTop}>
              <div className={styles.search}>
                <SearchBarListing
                  suggestions={listing?.suggestions || []}
                  placeholder={t('Cerca nome collezioni o prodotto')}
                  open={searchMobileOpen}
                  className={styles.searchBar}
                  setNoResults={setNoResults}
                  searchValue={searchValue}
                  setSearchValue={setSearchValue}
                  isFetching={isFetching}
                />

                <div className={styles.info}>
                  <span className={`${styles.resultsLabel}`}>
                    {t((listing.count || 0) === 1 ? '{0} risultato' : '{0} risultati', [
                      (listing.count || 0).toString(),
                    ])}
                  </span>
                  <div className={styles.chips}>
                    {filters.map((filter, index) => (
                      <Chip
                        label={
                          filter.label.includes(',#') ? filter.label.split(',')[0] : filter.label
                        }
                        key={`${filter.chiave}-${filter.id}`}
                        selected
                        showIcon
                        onClick={() => {
                          setFilters(
                            filters.filter(
                              (val) => !(val.id === filter.id && val.chiave === filter.chiave)
                            )
                          )
                        }}
                      />
                    ))}
                    {filters.length > 0 && (
                      <button className={styles.filtersReset} onClick={() => setFilters([])}>
                        {t('Azzera filtri')}
                      </button>
                    )}
                  </div>
                </div>

                <OrderByDropdown
                  ordinamenti={listing.ordinamenti}
                  orderBy={orderBy}
                  setOrderBy={setOrderBy}
                  className={styles.orderBy}
                />

                <button
                  onClick={() => setFiltersMobileOpen(true)}
                  className={styles.filtersMobileTrigger}
                >
                  {t('Filtra e Ordina')}
                  <Filter className={styles.iconMobile} />
                </button>

                <ButtonRounded
                  icon={searchMobileOpen ? <Close /> : <Search />}
                  theme="neutral20"
                  size="xs"
                  onClick={() => setSearchMobileOpen((prev) => !prev)}
                  className={styles.searchMobileTrigger}
                />
              </div>
              <div className={styles.listing}>{processedCards}</div>
            </div>
            <div className={styles.pagination}>
              <ResultsLabel num={processedCards.length} tot={listing.count} />
              {listing?.page < listing?.numPages ? (
                <Button onClick={loadMore} variant="ghost" label={t('Mostra di più')} />
              ) : null}
            </div>

            {categoria ? (
              <DescriptionListing
                title={categoria.titleTerzoLivello}
                description={categoria.description}
                descriptionContent={categoria.descriptionTerzoLivello}
              />
            ) : null}
          </div>
        </WContainer>
      </div>
      <ModalNoResult item={listing} isOpen={noResults} setIsOpen={setNoResults} />
      <ListingFiltersMobile
        item={listing}
        open={filtersMobileOpen}
        onClose={() => setFiltersMobileOpen(false)}
        filters={filters}
        setFilters={setFilters}
        orderBy={orderBy}
        setOrderBy={setOrderBy}
      />
    </>
  ) : (
    <></>
  )
}

export default ListingExplorer
